import { useState, useEffect, useRef, useCallback } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Plus, Search, Trash2, Eye, EyeOff, Check, X } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { superAdminUsersAPI, type UserListItem } from "../../services/api";
import { validatePassword } from "@/lib/passwordValidation";
import { useAuth } from "../../contexts/AuthContext";
import { formatDate } from "../../lib/utils";

export interface UserRow {
  id: string;
  name: string;
  email: string;
  role: string;
  joinedOn: string;
  phone_number?: string;
}

function toUserRow(item: UserListItem): UserRow {
  const name =
    item.full_name?.trim() ??
    [item.first_name, item.last_name].filter(Boolean).join(" ").trim() ??
    "-";
  return {
    id: item.id,
    name,
    email: item.email,
    role: item.jbf_role ?? "—",
    joinedOn: formatDate(item.created_on),
    phone_number: item.phone_number,
  };
}

const ADD_USER_ROLES = [
  { value: "student", label: "Student" },
  { value: "institutions_admin", label: "Institution Admin" },
  { value: "management", label: "Management" },
  { value: "trustee", label: "Trustee" },
  { value: "donor", label: "Donor" },
  { value: "hostel_admin", label: "Hostel Admin" },
] as const;

export default function UserManagement() {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [showAddUserPassword, setShowAddUserPassword] = useState(false);
  const [addUserForm, setAddUserForm] = useState({
    full_name: "",
    email: "",
    password: "",
    phone_number: "",
    jbf_role: "",
  });
  const [viewUser, setViewUser] = useState<UserRow | null>(null);
  const [deleteUser, setDeleteUser] = useState<UserRow | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const { can } = useAuth();
  const canWrite = can("users", "write");

  const fetchUsers = useCallback(async () => {
    setListLoading(true);
    try {
      const raw = await superAdminUsersAPI.list({
        ...(searchTerm.trim() ? { search: searchTerm.trim() } : {}),
        ...(roleFilter !== "all" ? { jbf_role: roleFilter } : {}),
      });
      const list = Array.isArray(raw)
        ? raw
        : (raw as { data: UserListItem[] })?.data;
      setUsers(Array.isArray(list) ? list.map(toUserRow) : []);
    } catch {
      setUsers([]);
    } finally {
      setListLoading(false);
    }
  }, [searchTerm, roleFilter]);

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      fetchUsers();
    }, 300);
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [fetchUsers]);

  const filteredUsers = users;

  const handleAddUser = async () => {
    const { full_name, email, password, phone_number, jbf_role } = addUserForm;
    if (!full_name.trim()) {
      toast.error("Please enter full name");
      return;
    }
    if (!email.trim()) {
      toast.error("Please enter email");
      return;
    }
    if (!password) {
      toast.error("Please enter password");
      return;
    }
    const passwordValidation = validatePassword(password.trim(), {
      email: email.trim(),
      fullName: full_name.trim(),
    });
    if (!passwordValidation.valid) {
      toast.error(passwordValidation.errors[0] ?? "Invalid password");
      return;
    }
    if (!jbf_role) {
      toast.error("Please select a role");
      return;
    }
    setAddUserLoading(true);
    try {
      const res = await superAdminUsersAPI.create({
        email: email.trim(),
        password: password.trim(),
        full_name: full_name.trim(),
        phone_number: phone_number.trim() || undefined,
        jbf_role,
      });
      toast.success("User added successfully!");
      setIsDialogOpen(false);
      setAddUserForm({
        full_name: "",
        email: "",
        password: "",
        phone_number: "",
        jbf_role: "",
      });
      // Response from /jbf/users/ POST typically returns the user object directly or in .data
      const newUser = res?.data ?? res;
      if (newUser?.id) {
        setUsers(prev => [
          ...prev,
          {
            id: newUser.id,
            name: full_name.trim(),
            email: newUser.email ?? email.trim(),
            phone_number: phone_number.trim() || undefined,
            role: jbf_role,
            joinedOn: formatDate(new Date()),
          },
        ]);
      }
      await fetchUsers();
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ??
        error?.response?.data?.error?.message ??
        error?.message ??
        "Failed to add user.";
      toast.error(msg);
    } finally {
      setAddUserLoading(false);
    }
  };

  const resetAddUserForm = () => {
    setAddUserForm({
      full_name: "",
      email: "",
      password: "",
      phone_number: "",
      jbf_role: "",
    });
  };

  const handleDeleteUser = async () => {
    if (!deleteUser) {
      return;
    }
    setDeleteLoading(true);
    try {
      await superAdminUsersAPI.delete(deleteUser.id);
      setUsers(prev => prev.filter(u => u.id !== deleteUser.id));
      toast.success("User deleted successfully!");
      setDeleteUser(null);
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ??
        error?.response?.data?.error?.message ??
        error?.message ??
        "Failed to delete user.";
      toast.error(msg);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            User Management
          </h1>
          <p className="text-muted-foreground">
            Manage all system users and their roles
          </p>
        </div>
        {canWrite && (
          <Dialog
            open={isDialogOpen}
            onOpenChange={open => {
              setIsDialogOpen(open);
              if (!open) {
                resetAddUserForm();
              }
            }}
          >
            <DialogTrigger asChild>
              <Button className="gap-2">
                <Plus className="h-4 w-4" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New User</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="userName">Full Name</Label>
                  <Input
                    id="userName"
                    placeholder="Enter full name"
                    value={addUserForm.full_name}
                    onChange={e =>
                      setAddUserForm(prev => ({
                        ...prev,
                        full_name: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="userEmail">Email</Label>
                  <Input
                    id="userEmail"
                    type="email"
                    placeholder="user@example.com"
                    value={addUserForm.email}
                    onChange={e =>
                      setAddUserForm(prev => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="userPhone">Phone Number</Label>
                  <Input
                    id="userPhone"
                    placeholder="Enter 10-digit phone"
                    value={addUserForm.phone_number}
                    maxLength={10}
                    onChange={e =>
                      setAddUserForm(prev => ({
                        ...prev,
                        phone_number: e.target.value.replace(/\D/g, ""),
                      }))
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="userPassword">Password</Label>
                  <div className="relative">
                    <Input
                      id="userPassword"
                      type={showAddUserPassword ? "text" : "password"}
                      placeholder="Enter password (8-10 characters)"
                      value={addUserForm.password}
                      onChange={e =>
                        setAddUserForm(prev => ({
                          ...prev,
                          password: e.target.value.slice(0, 10),
                        }))
                      }
                      maxLength={10}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowAddUserPassword(prev => !prev)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                      aria-label={
                        showAddUserPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showAddUserPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {addUserForm.password && (
                    <div className="mt-2 rounded-md border border-border bg-muted/30 p-3 text-xs">
                      <p className="mb-2 font-medium text-muted-foreground">
                        Password requirements
                      </p>
                      <ul className="space-y-1">
                        {validatePassword(addUserForm.password, {
                          email: addUserForm.email,
                          fullName: addUserForm.full_name,
                        }).hints.map(h => (
                          <li
                            key={h.rule}
                            className={`flex items-center gap-2 ${h.met ? "text-green-600" : "text-muted-foreground"}`}
                          >
                            {h.met ? (
                              <Check className="h-3.5 w-3.5 shrink-0" />
                            ) : (
                              <X className="h-3.5 w-3.5 shrink-0" />
                            )}
                            <span>{h.rule}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div>
                  <Label htmlFor="userRole">Role</Label>
                  <Select
                    value={addUserForm.jbf_role}
                    onValueChange={value =>
                      setAddUserForm(prev => ({ ...prev, jbf_role: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      {ADD_USER_ROLES.map(r => (
                        <SelectItem key={r.value} value={r.value}>
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                    disabled={addUserLoading}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleAddUser} disabled={addUserLoading}>
                    {addUserLoading ? (
                      <span className="flex items-center gap-2">
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                        Adding...
                      </span>
                    ) : (
                      "Add User"
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="student">Student</SelectItem>
                <SelectItem value="institutions_admin">
                  Institution Admin
                </SelectItem>
                <SelectItem value="management">Management</SelectItem>
                <SelectItem value="trustee">Trustee</SelectItem>
                <SelectItem value="donor">Donor</SelectItem>
                <SelectItem value="hostel_admin">Hostel Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Users ({filteredUsers.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {listLoading ? (
            <div className="flex justify-center py-12 text-muted-foreground">
              Loading users...
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Sr. No</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone Number</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Joined On</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user, index) => (
                  <TableRow key={user.id} className="hover:bg-muted">
                    <TableCell>{index + 1}</TableCell>
                    <TableCell className="font-medium">{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.phone_number ?? "-"}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="capitalize">
                        {user.role.replace(/_/g, " ")}
                      </Badge>
                    </TableCell>
                    <TableCell>{user.joinedOn}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setViewUser(user)}
                          aria-label="View user"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {canWrite && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setDeleteUser(user)}
                            aria-label="Delete user"
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* View User Dialog */}
      <Dialog
        open={!!viewUser}
        onOpenChange={open => !open && setViewUser(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
          </DialogHeader>
          {viewUser && (
            <div className="space-y-3 text-sm">
              <p>
                <span className="font-medium text-muted-foreground">Name:</span>{" "}
                {viewUser.name}
              </p>
              <p>
                <span className="font-medium text-muted-foreground">
                  Email:
                </span>{" "}
                {viewUser.email}
              </p>
              <p>
                <span className="font-medium text-muted-foreground">
                  Phone:
                </span>{" "}
                {viewUser.phone_number ?? "-"}
              </p>
              <p>
                <span className="font-medium text-muted-foreground">Role:</span>{" "}
                {viewUser.role.replace(/_/g, " ")}
              </p>
              <p>
                <span className="font-medium text-muted-foreground">
                  Joined On:
                </span>{" "}
                {viewUser.joinedOn}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteUser}
        onOpenChange={open => !open && setDeleteUser(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
          </DialogHeader>
          {deleteUser && (
            <>
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete{" "}
                <strong>{deleteUser.name}</strong> ({deleteUser.email})? This
                action cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDeleteUser(null)}
                  disabled={deleteLoading}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteUser}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? "Deleting..." : "Delete"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
